import { useState } from "react";
import { Check } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactForm({
  accentColor = "#1B5F7E",
  successBg = "#EBF5FB",
  successBorder = "#D0E8F5",
  origen = "contacto",
  initialMensaje = "",
  submitLabel = "Enviar mensaje",
  successTitle = "¡Mensaje enviado!",
  successText = "Te responderemos en menos de 24 horas.",
  testPrefix = "contacto",
}) {
  const [form, setForm] = useState({
    nombre: "",
    apellidos: "",
    telefono: "",
    email: "",
    mensaje: initialMensaje,
    comunicaciones_comerciales: false,
    origen,
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${BACKEND_URL}/api/contacto`, { ...form, origen });
      setStatus("success");
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-5 py-4 rounded-xl border border-slate-200 bg-[#F8FAFC] focus:outline-none focus:ring-2 text-sm";

  const focusStyle = {
    "--tw-ring-color": `${accentColor}1A`,
    borderColor: "#E2E8F0",
  };

  if (status === "success") {
    return (
      <div
        data-testid={`${testPrefix}-success-message`}
        className="rounded-2xl p-12 text-center"
        style={{ background: successBg, border: `1px solid ${successBorder}` }}
      >
        <Check size={40} className="mx-auto mb-5" style={{ color: accentColor }} />
        <h3 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-2xl font-semibold mb-3">
          {successTitle}
        </h3>
        <p className="text-slate-500 text-sm">{successText}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid={`${testPrefix}-form`} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">Nombre *</label>
          <input
            type="text"
            placeholder="Tu nombre"
            required
            value={form.nombre}
            onChange={(e) => updateForm("nombre", e.target.value)}
            data-testid={`${testPrefix}-nombre-input`}
            className={inputClass}
            style={focusStyle}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">Apellidos *</label>
          <input
            type="text"
            placeholder="Tus apellidos"
            required
            value={form.apellidos}
            onChange={(e) => updateForm("apellidos", e.target.value)}
            data-testid={`${testPrefix}-apellidos-input`}
            className={inputClass}
            style={focusStyle}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">Teléfono</label>
          <input
            type="tel"
            placeholder="+34 600 000 000"
            value={form.telefono}
            onChange={(e) => updateForm("telefono", e.target.value)}
            data-testid={`${testPrefix}-telefono-input`}
            className={inputClass}
            style={focusStyle}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">Email *</label>
          <input
            type="email"
            placeholder="email@ejemplo.com"
            required
            value={form.email}
            onChange={(e) => updateForm("email", e.target.value)}
            data-testid={`${testPrefix}-email-input`}
            className={inputClass}
            style={focusStyle}
          />
        </div>
      </div>
      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Algo que nos quieras comentar</label>
        <textarea
          placeholder="Cuéntanos en qué podemos ayudarte..."
          value={form.mensaje}
          onChange={(e) => updateForm("mensaje", e.target.value)}
          data-testid={`${testPrefix}-mensaje-textarea`}
          rows={5}
          className={`${inputClass} resize-none`}
          style={focusStyle}
        />
      </div>
      <label className="flex items-start gap-3 cursor-pointer" data-testid={`${testPrefix}-comercial-checkbox`}>
        <input
          type="checkbox"
          required
          checked={form.comunicaciones_comerciales}
          onChange={(e) => updateForm("comunicaciones_comerciales", e.target.checked)}
          className="mt-1 w-4 h-4 rounded border-slate-300 focus:ring-2"
          style={{ accentColor }}
        />
        <span className="text-sm text-slate-600">
          Quiero recibir comunicaciones comerciales sobre DAC-PAC
        </span>
      </label>
      {status === "error" && (
        <p className="text-red-500 text-sm">Ha ocurrido un error. Inténtalo de nuevo o escríbenos directamente.</p>
      )}
      <button
        type="submit"
        disabled={loading}
        data-testid={`${testPrefix}-submit-btn`}
        className="btn-primary w-full justify-center py-4 disabled:opacity-60"
        style={{ background: accentColor }}
      >
        {loading ? "Enviando..." : submitLabel}
      </button>
    </form>
  );
}
